import { useEffect, useMemo, useRef, useState } from 'react';
import type { DemoDockAuthState } from './auth';
import { getDemoDockProxyBaseUrl, getDemoDockProxyUrl } from './auth';
import type { EditablePageElement, ReplaceTextCommand } from './processPrompt';

type PreviewFrameProps = {
  auth: DemoDockAuthState;
  targetUrl: string;
  onTargetUrlChange: (url: string) => void;
  outboundCommand: ReplaceTextCommand | null;
  onPageContextChange: (elements: EditablePageElement[]) => void;
};

const previewRequestTimeoutMs = 10000;

export function PreviewFrame({
  auth,
  targetUrl,
  onTargetUrlChange,
  outboundCommand,
  onPageContextChange,
}: PreviewFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [remoteHtml, setRemoteHtml] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const { refresh } = auth;

  const proxyBaseUrl = useMemo(getDemoDockProxyBaseUrl, []);

  const proxyUrl = useMemo(() => {
    const encodedUrl = encodeURIComponent(targetUrl);
    return getDemoDockProxyUrl(`/api/proxy?url=${encodedUrl}`);
  }, [targetUrl]);

  useEffect(() => {
    if (!proxyBaseUrl || auth.status !== 'authenticated') {
      setRemoteHtml(null);
      setPreviewError(null);
      return;
    }

    const controller = new AbortController();
    let didTimeout = false;
    const timeout = window.setTimeout(() => {
      didTimeout = true;
      controller.abort();
    }, previewRequestTimeoutMs);
    setRemoteHtml(null);
    setPreviewError(null);

    fetch(proxyUrl, {
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.status === 401 || response.redirected) {
          refresh();
          throw new Error('AUTH_REQUIRED');
        }

        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || `Proxy returned ${response.status}`);
        }

        return response.text();
      })
      .then((html) => {
        setRemoteHtml(html);
      })
      .catch((err) => {
        if (controller.signal.aborted && !didTimeout) return;
        if (didTimeout) {
          setPreviewError('The Demo Dock proxy did not respond in time. Check the deployed backend and try again.');
          return;
        }
        setPreviewError(err instanceof Error ? err.message : 'Unable to load preview');
      })
      .finally(() => {
        window.clearTimeout(timeout);
      });

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [auth.status, proxyBaseUrl, proxyUrl, refresh]);

  useEffect(() => {
    if (!outboundCommand) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;

    win.postMessage(
      {
        type: 'DEMO_DOCK_COMMAND',
        payload: outboundCommand,
      },
      '*'
    );
  }, [outboundCommand]);

  const capturePageContext = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) {
      onPageContextChange([]);
      return;
    }

    const createSelector = (element: Element) => {
      if (element.id && typeof CSS !== 'undefined' && CSS.escape) {
        return `#${CSS.escape(element.id)}`;
      }

      const parts: string[] = [];
      let current: Element | null = element;
      while (current && current !== doc.body && parts.length < 4) {
        const tag = current.tagName.toLowerCase();
        const tagName = current.tagName;
        const siblings = current.parentElement
          ? Array.from(current.parentElement.children).filter((child) => child.tagName === tagName)
          : [];
        const index = Math.max(1, siblings.indexOf(current) + 1);
        parts.unshift(`${tag}:nth-of-type(${index})`);
        current = current.parentElement;
      }
      return parts.join(' > ');
    };

    const elements = Array.from(doc.querySelectorAll('h1, h2, h3, p, button, a'))
      .filter((element) => !element.closest('script, style, noscript'))
      .map((element) => ({
        selector: createSelector(element),
        text: (element.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 240),
      }))
      .filter((element) => element.selector && element.text)
      .slice(0, 40);

    onPageContextChange(elements);
  };

  return (
    <div className="demo-dock-preview">
      <div className="demo-dock-target-bar">
        <label htmlFor="demo-dock-target-url">Target URL</label>
        <input
          id="demo-dock-target-url"
          type="url"
          value={targetUrl}
          onChange={(e) => onTargetUrlChange(e.target.value)}
        />
      </div>

      <div className="demo-dock-frame-shell">
        {proxyBaseUrl && auth.status === 'loading' ? (
          <div className="demo-dock-auth-panel">
            <h2>Checking sign-in</h2>
            <p>Demo Dock is checking whether your account can load the preview.</p>
          </div>
        ) : proxyBaseUrl && auth.status !== 'authenticated' ? (
          <div className="demo-dock-auth-panel">
            <h2>Sign in to load Demo Dock</h2>
            <p>The Demo Dock proxy is protected. Sign in with your account, then return here.</p>
            {auth.loginScreenUrl ? <a href={auth.loginScreenUrl}>Sign in</a> : null}
          </div>
        ) : previewError ? (
          <div className="demo-dock-auth-panel">
            <h2>Preview unavailable</h2>
            <p>
              {previewError === 'AUTH_REQUIRED'
                ? 'The Demo Dock proxy is protected. Sign in with your account, then return here.'
                : previewError}
            </p>
            {auth.loginScreenUrl ? <a href={auth.loginScreenUrl}>Sign in</a> : null}
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            title="Proxied site preview"
            src={proxyBaseUrl ? undefined : proxyUrl}
            srcDoc={
              proxyBaseUrl ? remoteHtml ?? '<!doctype html><p>Loading preview...</p>' : undefined
            }
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            onLoad={capturePageContext}
          />
        )}
      </div>
    </div>
  );
}
